import { Icon, IconButton, Tooltip } from '@mui/material';
import {
  GET_THREADS_AGGREGATE_QUERY,
  GET_THREADS_QUERY,
  GetThreadsAggregateParams,
  RootEntityType,
  ThreadAggregateOptions,
  ThreadStatus,
  UPDATE_THREAD_MUTATION,
  useGetThreadsAggregateQuery,
  useUpdateThread,
} from '@schooly/api';
import { useNotifications } from '@schooly/components/notifications';
import { useFlag } from '@schooly/hooks/use-flag';
import { ClosedThreadIcon, SimpleButton, Spin, ThreadIcon } from '@schooly/style';
import { useIsMutating, useQueryClient } from '@tanstack/react-query';
import { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

type UpdateAllThreadsComponentProps = {
  schoolId: string;
  messageId: string;
  rootEntityType: RootEntityType;
};

export const UpdateAllThreadsComponent: FC<UpdateAllThreadsComponentProps> = ({
  schoolId,
  messageId,
  rootEntityType,
}) => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const updateThreadMutation = useUpdateThread();

  const isThreadUpdating = useIsMutating({
    mutationKey: [UPDATE_THREAD_MUTATION],
  });

  const { showError } = useNotifications();

  const aggregateParams = useMemo(
    () =>
      ({
        school_id: schoolId,
        root_entity_type: rootEntityType,
        root_entity_id: messageId,
        include: [ThreadAggregateOptions.ROOT_ENTITY_STATUS],
      }) as GetThreadsAggregateParams,
    [schoolId, rootEntityType, messageId],
  );

  const { data: aggregateData, isFetching } = useGetThreadsAggregateQuery(aggregateParams, {
    refetchOnMount: 'always',
  });

  const rootEntityStatus = aggregateData?.data?.[messageId]?.root_entity_status;

  const handleUpdateThreadStatus = useCallback(async () => {
    if (!rootEntityStatus) return;

    await updateThreadMutation.mutateAsync(
      {
        school_id: schoolId,
        root_entity_type: rootEntityType,
        root_entity_id: messageId,
        status:
          rootEntityStatus === ThreadStatus.ARCHIVED ? ThreadStatus.ACTIVE : ThreadStatus.ARCHIVED,
      },
      {
        onError: showError,
      },
    );

    queryClient.invalidateQueries({
      queryKey: [GET_THREADS_AGGREGATE_QUERY, aggregateParams],
    });
    queryClient.invalidateQueries({
      queryKey: [
        GET_THREADS_QUERY,
        {
          school_id: schoolId,
          root_entity_type: rootEntityType,
          root_entity_id: messageId,
        },
      ],
    });
  }, [
    rootEntityStatus,
    updateThreadMutation,
    schoolId,
    rootEntityType,
    messageId,
    showError,
    queryClient,
    aggregateParams,
  ]);

  if (!rootEntityStatus || isFetching || isThreadUpdating) {
    return (
      <Icon>
        <Spin />
      </Icon>
    );
  }

  const isAllThreadsClosed = rootEntityStatus === ThreadStatus.ARCHIVED;

  return (
    <UpdateThreadComponent
      onThreadStatusUpdate={handleUpdateThreadStatus}
      label={formatMessage(
        { id: 'messages-AllThreadsAction' },
        {
          action: formatMessage({
            id: isAllThreadsClosed ? 'action-Open' : 'action-Close',
          }),
        },
      )}
      isThreadClosed={isAllThreadsClosed}
    />
  );
};

type UpdateThreadComponentProps = {
  onThreadStatusUpdate: () => void;
  label: string;
  isThreadClosed: boolean;
  disabled?: boolean;
  isLoading?: boolean;
};

export const UpdateThreadComponent: FC<UpdateThreadComponentProps> = ({
  onThreadStatusUpdate,
  label,
  isThreadClosed,
  isLoading,
}) => {
  const [opened, open, close] = useFlag();

  return (
    <div>
      <Tooltip
        disableTouchListener
        onOpen={open}
        onClose={close}
        title={
          <SimpleButton
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onThreadStatusUpdate();
            }}
            key={label}
            startIcon={isThreadClosed ? <ThreadIcon /> : <ClosedThreadIcon />}
          >
            {label}
          </SimpleButton>
        }
        placement="bottom"
        arrow={false}
        componentsProps={{
          tooltip: {
            sx: {
              minWidth: 164,
              maxWidth: 224,
              borderRadius: 0.5,
              '&&.MuiTooltip-tooltip': {
                margin: 0.25,
              },
            },
          },
        }}
      >
        <IconButton
          disabled={isLoading}
          sx={{
            color: (theme) => (opened ? `${theme.palette.primary.main} !important` : undefined),
          }}
          inverse
        >
          {isThreadClosed ? <ClosedThreadIcon /> : <ThreadIcon />}
        </IconButton>
      </Tooltip>
    </div>
  );
};
